import { useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-toastify';
import {
  getClinicas,
  getPacientes,
  getAtendimentos,
  createAtendimento,
  updatePacienteObservacao,
  createPaciente,
} from '../services/atendimentoService';
import { auth } from '../firebase';

const useAtendimentoData = () => {
  const [activePage, setActivePage] = useState('atendimento');
  const [nomePaciente, setNomePaciente] = useState('');
  const [clinica, setClinica] = useState('');
  const [dataAtendimento, setDataAtendimento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [historicoAtendimentos, setHistoricoAtendimentos] = useState([]);
  const [clinicas, setClinicas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [isObservacaoModalOpen, setIsObservacaoModalOpen] = useState(false);
  const [modalObservacao, setModalObservacao] = useState('');
  const [user] = useAuthState(auth);

  // Função para carregar atendimentos do Firebase
  const loadAtendimentos = async () => {
    try {
      const atendimentos = await getAtendimentos(user.uid);
      setHistoricoAtendimentos(atendimentos); // Atualiza o estado com os dados reais do Firebase
    } catch (error) {
      toast.error('Erro ao buscar atendimentos do Firebase.');
    }
  };

  // Carregar dados do Firebase ao montar
  useEffect(() => {
    if (user) {
      getClinicas(user.uid)
        .then(setClinicas)
        .catch(() => toast.error('Erro ao buscar clínicas'));
      getPacientes(user.uid)
        .then(setPacientes)
        .catch(() => toast.error('Erro ao buscar pacientes'));

      loadAtendimentos(); // Carregar atendimentos do Firebase
    }
  }, [user]);

  // Função para registrar atendimento
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    // Valida os campos obrigatórios
    if (!nomePaciente || !clinica || !dataAtendimento || !observacao.trim()) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      const atendimentoDate = dataAtendimento
        ? new Date(dataAtendimento)
        : new Date();

      // Criação do atendimento no Firebase
      await createAtendimento({
        nomePaciente,
        clinica,
        userId: user.uid,
        dataAtendimento: atendimentoDate,
        observacao,
      });

      // Verifica se o paciente já existe
      const pacienteExistente = pacientes.find((p) => p.nome === nomePaciente);
      if (pacienteExistente) {
        // Atualiza observação do paciente existente
        await updatePacienteObservacao(pacienteExistente.id, observacao);
      } else {
        // Cria novo paciente se não existir
        await createPaciente({
          nome: nomePaciente,
          clinica,
          userId: user.uid,
          observacoes: observacao,
        });
      }

      // Recarrega o histórico de atendimentos diretamente do Firebase
      await loadAtendimentos(); // Garante que estamos usando os dados reais salvos no Firebase

      // Limpa o formulário após submissão bem-sucedida
      setNomePaciente('');
      setClinica('');
      setDataAtendimento('');
      setObservacao('');
      toast.success('Atendimento registrado com sucesso!');
    } catch (error) {
      console.error('Erro ao registrar atendimento:', error);
      toast.error('Erro ao registrar atendimento. Tente novamente.');
    }
  };

  const handleObservacaoModalOpen = useCallback(
    (observacaoExistente, isHistory = false) => {
      if (isHistory) {
        setModalObservacao(observacaoExistente || '');
      } else {
        setModalObservacao(observacaoExistente || getFormattedAnamnesis());
      }
      setIsObservacaoModalOpen(true);
    },
    []
  );

  const handleUseObservacao = () => {
    setObservacao(modalObservacao);
    setIsObservacaoModalOpen(false);
  };

  const getFormattedAnamnesis = () => `
    <h3>Identificação:</h3><br>
    <h3>Queixa Principal (QP):</h3><br>
    <h3>História da Moléstia Atual (HMA):</h3><br>
    <h3>Revisão de Sistemas:</h3><br>
    <h3>História Patológica Pregressa:</h3><br>
    <h3>História Familiar:</h3><br>
    <h3>História Social/Hábitos de Vida:</h3>
  `;

  return {
    activePage,
    setActivePage,
    nomePaciente,
    setNomePaciente,
    clinica,
    setClinica,
    dataAtendimento,
    setDataAtendimento,
    observacao,
    setObservacao,
    historicoAtendimentos,
    setHistoricoAtendimentos,
    clinicas,
    pacientes,
    isObservacaoModalOpen,
    setIsObservacaoModalOpen,
    modalObservacao,
    setModalObservacao,
    handleSubmit,
    handleObservacaoModalOpen,
    handleUseObservacao,
  };
};

export default useAtendimentoData;
